<template>
  <div class="custom-content-height analytics-layout">
    <div class="row my-0 mx-1 px-0 px-sm-4 analytics-header">
      <div
        class="poppins text-dark-65 font-size-h1 display5-md font-weight-bolder col-12 col-md-4 mb-md-0"
      >
        <div
          :class="[
            'd-flex d-md-block justify-space-between align-center',
            { 'text-right': isRTL },
          ]"
        >
          <span>{{ $t("my_analytics") }}</span>
          <v-btn
            v-if="isMobileView"
            small
            class="filter-toggle-btn text-capitalize ml-2"
            outlined
            @click="isMobileFilterOpen = !isMobileFilterOpen"
          >
            <v-icon left size="18">mdi-filter-variant</v-icon>
            {{ isMobileFilterOpen ? $t("hide_filters") : $t("show_filters") }}
          </v-btn>
        </div>
      </div>
      <div class="col-12 col-md-8 p-0 px-3">
        <div
          v-show="!isMobileView || isMobileFilterOpen"
          :class="[
            'header-controls',
            {
              'mobile-filters-open-spacing': isMobileView && isMobileFilterOpen,
            },
          ]"
        >
          <FilterSelector
            :filters="filters"
            :update-data-of-charts="updateDataOfCharts"
          />
          <div class="date-picker-wrapper">
            <date-range-picker
              class="w-100 analytics-date-picker"
              ref="picker"
              :opens="datePickerPosition"
              :locale-data="{ firstDay: 1, format: 'dd/mm/yyyy' }"
              :close-on-esc="false"
              :showDropdowns="true"
              :ranges="ranges"
              min-date="01-01-2018"
              :max-date="new Date().toLocaleDateString()"
              control-container-class="form-control max-h-45px"
              v-model="dateRange"
            >
              <template v-slot:input="picker">
                <div class="h-100 d-flex align-center">
                  <span class="svg-icon svg-icon-2 mr-2">
                    <v-icon size="22">mdi-calendar</v-icon>
                  </span>
                  <input
                    type="text"
                    class="custom-input"
                    :placeholder="$t('select_a_date')"
                    :value="picker.rangeText === ' - ' ? '' : picker.rangeText"
                  />
                </div>
              </template>

              <template v-slot:footer="picker">
                <div class="drp-buttons py-4 px-6 poppins">
                  <button
                    type="button"
                    class="btn btn-light ls1 py-2 px-7"
                    @click="picker.clickCancel"
                  >
                    {{ $t("cancel") }}
                  </button>
                  <button
                    type="button"
                    class="btn btn--export-filter ls1 py-2 px-4"
                    @click="picker.clickApply"
                  >
                    {{ $t("apply") }}
                  </button>
                </div>
              </template>
            </date-range-picker>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showData">
      <div class="row mx-0 mx-sm-1 mt-md-4 px-2 px-sm-4 analytics-grid">
        <analytics-orders
          v-if="userData.length > 0"
          :item="userData[0]"
          :filterData="dataOfFilters"
          :exportChart="loadDataFromServer"
        />
        <analytics-inventory
          v-if="userData.length > 1"
          :item="userData[1]"
          :filterData="dataOfFilters"
          :exportChart="loadDataFromServer"
        />
      </div>
    </div>
  </div>
</template>

<script>
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

import ApiService from "@/core/services/api.service";
import AnalyticsOrders from "@/own/components/dashboard/AnalyticsOrdersNew";
import AnalyticsInventory from "@/own/components/dashboard/AnalyticsInventoryNew";
import FilterSelector from "@/own/components/dashboard/filters/FilterSelector";
import { SET_PAGE_LOADING } from "@/core/services/store/config.module";
import { isRTLScreen } from "@/core/utils/general.util";

export default {
  name: "AnalyticsLayout",
  props: ["userData", "updateDashboardData", "filters"],
  components: {
    FilterSelector,
    AnalyticsOrders,
    AnalyticsInventory,
    DateRangePicker,
  },
  data() {
    return {
      dialog: false,
      dateTypeTrigger: "today",
      isVisible: true,
      dateType: "custom",
      dateRange: {
        startDate: null,
        endDate: null,
      },
      filterData: {},
      isMobileFilterOpen: false,
      dateTypes: [
        { text: "Today", value: "today" },
        { text: "This week", value: "this_week" },
        { text: "Last week", value: "last_week" },
        { text: "Last 7 days", value: "last_7_days" },
        { text: "This month", value: "this_month" },
        { text: "Last month", value: "last_month" },
        { text: "Last 30 days", value: "last_30_days" },
        { text: "This year", value: "this_year" },
        { text: "Last year", value: "last_year" },
        { text: "All time", value: "all_time" },
        { text: "Custom", value: "custom" },
      ],
    };
  },
  created() {
    const today = this.getToday();
    this.dateRange.startDate = today;
    this.dateRange.endDate = today;
  },
  methods: {
    getToday() {
      const date = new Date();
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    updateDataOfCharts(val) {
      const [key, value] = Object.entries(val)[0];
      this.filterData[key] = value;
      this.filterData = { ...this.filterData }; // force reactivity
      this.updateDashboardData(this.dataOfFilters);
      if (this.isMobileView) {
        this.isMobileFilterOpen = false;
      }
    },
    updateDateOfCharts(val) {
      if (this.dateType !== val) {
        this.isVisible = false;
      }
      if (val === "custom") {
        if (!this.dialog) {
          this.dialog = true;
          this.isVisible = false;
          return;
        } else {
          this.dialog = false;
          this.dateType = this.dateTypeTrigger;
          this.updateDashboardData(this.dataOfFilters);
          this.isVisible = true;
          return;
        }
      } else {
        this.dateTypeTrigger = val;
        this.dateType = val;
        this.updateDashboardData(this.dataOfFilters);
        this.isVisible = true;
      }
    },
    loadDataFromServer(url) {
      this.$store.commit(SET_PAGE_LOADING, true);
      ApiService.post(url, this.dataOfFilters)
        .then((response) => {
          const link = document.createElement("a");
          link.href = response.data.url;
          link.download = "Export.csv";
          link.click();
          setTimeout(() => {
            this.$store.commit(SET_PAGE_LOADING, false);
          }, 1500);
        })
        .catch(() => {
          this.$store.commit(SET_PAGE_LOADING, false);
        });
    },
    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, "0");
      const day = d.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
  computed: {
    dataOfFilters() {
      const today = this.getToday();
      const formattedStart = this.formatDate(this.dateRange.startDate);
      const formattedEnd = this.formatDate(this.dateRange.endDate);
      const dateRangeType =
        formattedStart === today && formattedEnd === today
          ? "today"
          : this.dateType;

      return {
        date_range_type: dateRangeType,
        date_range: [formattedStart, formattedEnd],
        ...this.filterData,
      };
    },
    showData() {
      return this.isVisible;
    },
    isDataReady() {
      return this.userData.length > 0;
    },
    isMobileView() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    datePickerPosition: function () {
      if (this.$vuetify.breakpoint.mdAndUp) {
        return "left";
      }
      return "center";
    },
    ranges: function () {
      let today = new Date();
      today.setHours(0, 0, 0, 0);

      let yesterday = new Date();
      yesterday.setDate(today.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);

      return {
        [this.$t("today")]: [today, today],
        [this.$t("yesterday")]: [yesterday, yesterday],
        [this.$t("last_7_days")]: [
          new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("last_30_days")]: [
          new Date(today.getFullYear(), today.getMonth(), today.getDate() - 30),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("this_month")]: [
          new Date(today.getFullYear(), today.getMonth(), 1),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("last_month")]: [
          new Date(today.getFullYear(), today.getMonth() - 1, 1),
          new Date(today.getFullYear(), today.getMonth(), 0),
        ],
        [this.$t("this_year")]: [
          new Date(today.getFullYear(), 0, 1),
          new Date(today.getFullYear(), today.getMonth(), today.getDate()),
        ],
        [this.$t("last_year")]: [
          new Date(today.getFullYear() - 1, 0, 1),
          new Date(today.getFullYear() - 1, 12, 31),
        ],
      };
    },
    isRTL() {
      return isRTLScreen(this.$i18n.locale);
    },
  },
};
</script>

<style scoped>
.rounded-md {
  border-radius: 0.6rem;
}

.shadow-sm {
  box-shadow: rgb(76 78 100 / 12%) 0px 2px 8px 0px !important;
}

.bg-light-primary {
  background-color: rgba(115, 34, 176, 0.1);
}

.bg-light-success {
  background-color: rgba(115, 34, 176, 0.15);
}

.bg-light-info {
  background-color: rgba(195, 44, 128, 0.1);
}

.bg-light-warning {
  background-color: rgba(195, 44, 128, 0.15);
}

.text-dark-50 {
  color: rgba(0, 0, 0, 0.5);
}

.text-dark-75 {
  color: rgba(0, 0, 0, 0.75);
}

.max-h-45px {
  max-height: 45px;
}

.analytics-layout {
  row-gap: 1.5rem;
  justify-content: flex-start;
}

.analytics-header {
  row-gap: 1rem;
}

@media (min-width: 768px) {
  .analytics-header {
    align-items: center;
  }
}

.header-controls {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
}

.filter-toggle-btn {
  border-color: rgba(115, 34, 176, 0.35) !important;
  color: #7322b0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  min-height: 34px !important;
  padding: 0 10px !important;
  line-height: 1 !important;
}

.filter-toggle-btn .v-btn__content {
  display: inline-flex !important;
  align-items: center !important;
  gap: 6px;
}

.filter-toggle-btn .v-icon {
  margin: 0 !important;
  line-height: 1 !important;
}

@media (min-width: 768px) {
  .header-controls {
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    gap: 1rem;
  }
}

.date-picker-wrapper {
  width: 100%;
}

@media (min-width: 768px) {
  .date-picker-wrapper {
    max-width: 320px;
  }
}

::v-deep .analytics-date-picker .form-control {
  min-height: 45px;
  border: 1px solid #e4e6ef !important;
  border-radius: 0.42rem !important;
}

.analytics-grid {
  row-gap: 1.5rem;
  align-items: flex-start;
}

@media (max-width: 767px) {
  .analytics-header {
    row-gap: 0.5rem;
  }

  .analytics-header .display5-md {
    margin-bottom: 0 !important;
  }

  .analytics-header .col-12.col-md-4 > .d-flex {
    min-height: 34px;
  }

  .mobile-filters-open-spacing {
    margin-bottom: 0.75rem;
  }

  /* .analytics-grid {
    margin-top: 1rem !important;
  } */

  .analytics-layout {
    padding-top: 0.25rem;
  }
}
</style>

<style>
@media (max-width: 767px) {
  .daterangepicker {
    width: calc(100vw - 3rem) !important;
    max-width: 360px !important;
    min-width: 0 !important;
    left: 50% !important;
    right: auto !important;
    transform: translateX(-50%) !important;
    overflow-x: hidden !important;
    overflow-y: visible !important;
  }

  /* Preset ranges — horizontal scrollable chips at the top */
  .daterangepicker .ranges {
    display: block !important;
    width: 100% !important;
    float: none !important;
    border-bottom: 1px solid #e9ecef !important;
    padding: 10px 12px !important;
    box-sizing: border-box !important;
  }

  /* Ensure calendars wrapper is visible */
  .daterangepicker .calendars {
    display: block !important;
    width: 100% !important;
    float: none !important;
  }

  .daterangepicker .ranges ul {
    display: flex !important;
    flex-direction: row !important;
    flex-wrap: nowrap !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
    scrollbar-width: none !important;
    gap: 6px !important;
    padding: 2px 0 6px !important;
    margin: 0 !important;
    list-style: none !important;
  }

  .daterangepicker .ranges ul::-webkit-scrollbar {
    display: none !important;
  }

  .daterangepicker .ranges ul li {
    flex: 0 0 auto !important;
    padding: 4px 12px !important;
    border-radius: 20px !important;
    border: 1px solid #e4e6ef !important;
    background: #f5f5f5 !important;
    color: #3f4254 !important;
    font-size: 12px !important;
    white-space: nowrap !important;
    cursor: pointer !important;
  }

  .daterangepicker .ranges ul li.active {
    background: #7322b0 !important;
    border-color: #7322b0 !important;
    color: #fff !important;
  }

  /* Stack calendars top / bottom */
  .daterangepicker .calendars-container {
    display: flex !important;
    flex-direction: column !important;
    width: 100% !important;
  }

  .daterangepicker .drp-calendar {
    float: none !important;
    display: block !important;
    width: 100% !important;
    max-width: 100% !important;
    padding: 8px 16px !important;
    box-sizing: border-box !important;
  }

  .daterangepicker .drp-calendar.left {
    border-right: none !important;
    border-bottom: 1px solid #e9ecef !important;
  }

  .daterangepicker table {
    width: 100% !important;
  }
}
</style>
